/* eslint-disable max-statements */
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Divider,
  Grid,
  Group,
  LoadingOverlay,
  Paper,
  ScrollArea,
  Stack,
  Switch,
  Table,
  Text,
  TextInput as MantineTextInput,
  ThemeIcon,
  useMantineTheme,
} from '@mantine/core';
import { IconAlertCircle, IconCircleCheckFilled, IconFlask, IconInfoCircle, IconRefresh, IconSearch, IconXboxXFilled } from '@tabler/icons-react';
import { useMemo, useState } from 'react';

import { Batch, EnvironmentCountries } from '@e-origin/shared';

import { NumberInput, Select } from '@components';
import { useSetSimulatorStatus } from '@hooks/batch/simulator/useSetSimulatorStatus';
import { useSimulatorDeclarationGroups } from '@hooks/batch/simulator/useSimulatorDeclarationGroups';
import { useSimulatorStatuses } from '@hooks/batch/simulator/useSimulatorStatuses';
import { useSettings } from '@hooks/useSettings';
import { useSimulatorConfig } from './useSimulatorConfig';

interface CustomsMessageSimulatorProps {
  batch: Batch;
  onHide: () => void;
}

interface LrnGroup {
  lrn: string;
  sequences: string[];
  currentStatus?: string;
}

interface LrnConfig {
  status?: string;
  useCallback: boolean;
  callbackDelay: number;
  isOverrideEnabled: boolean;
}

const prettifyStatus = (status?: string) => {
  if (!status) return 'Not set';

  return status
    .toLowerCase()
    .split('_')
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');
};

const getStatusTone = (status?: string) => {
  const normalizedStatus = status?.toLowerCase();

  if (!normalizedStatus) return 'gray';
  if (normalizedStatus.includes('reject') || normalizedStatus.includes('error')) return 'red';
  if (normalizedStatus.includes('control') || normalizedStatus.includes('wait')) return 'yellow';
  if (normalizedStatus.includes('accept') || normalizedStatus.includes('sent') || normalizedStatus.includes('release') || normalizedStatus.includes('ack'))
    return 'green';

  return 'blue';
};

export const CustomsMessageSimulator = ({ batch, onHide }: CustomsMessageSimulatorProps) => {
  const { data: lrnGroups, isLoading, error } = useSimulatorDeclarationGroups(batch._id);
  const { data: statuses, isLoading: isLoadingStatuses } = useSimulatorStatuses(batch._id);
  const { data: settings } = useSettings();
  const setStatusMutation = useSetSimulatorStatus();
  const [searchValue, setSearchValue] = useState('');

  const theme = useMantineTheme();

  const {
    lrnConfigs,
    applyAllConfig,
    setApplyAllConfig,
    handleApplyToAll,
    handleConfirmChanges,
    updateLrnConfig,
    hasChanges,
    resetApplyAllConfig,
    resetLrnConfig,
    initialConfig,
  } = useSimulatorConfig({
    lrnGroups: lrnGroups as LrnGroup[],
    onSuccess: onHide,
    mutateFn: setStatusMutation.mutateAsync as any,
  });

  const statusOptions =
    statuses?.map((status) => ({
      value: status,
      label: prettifyStatus(status),
    })) || [];

  const filteredGroups = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    if (!normalizedSearch) {
      return (lrnGroups as LrnGroup[]) || [];
    }

    return ((lrnGroups as LrnGroup[]) || []).filter(
      (group) =>
        group.lrn.toLowerCase().includes(normalizedSearch) ||
        group.currentStatus?.toLowerCase().includes(normalizedSearch) ||
        group.sequences.some((sequence) => String(sequence).includes(normalizedSearch)),
    );
  }, [lrnGroups, searchValue]);

  const isCallbackDisabled = settings?.country === EnvironmentCountries.NL;

  const handleToggleRowOverride = (lrn: string, isEnabled: boolean) => {
    updateLrnConfig(lrn, {
      ...applyAllConfig,
      ...lrnConfigs[lrn],
      isOverrideEnabled: isEnabled,
    });
  };

  if (error) {
    return (
      <Stack gap="lg">
        <Alert icon={<IconAlertCircle size={20} />} title="Error Loading Data" color="red" variant="light">
          Failed to load declaration groups. Please try again or contact support if the issue persists.
        </Alert>
        <Divider />
        <Button onClick={onHide} variant="subtle" size="md">
          Close
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap="md" pos="relative">
      <LoadingOverlay visible={isLoading || isLoadingStatuses} overlayProps={{ radius: 'sm', blur: 2 }} />

      <Paper withBorder radius="md" p="lg">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group align="flex-start" gap="md" wrap="nowrap">
            <ThemeIcon size={42} radius="xl" variant="light" color="blue">
              <IconFlask size={22} />
            </ThemeIcon>

            <Stack gap={4}>
              <Group gap="sm">
                <Text fw={700} size="xl">
                  Batch #{batch.counter}
                </Text>
                <Badge variant="light" radius="xl" color="blue">
                  {batch.name}
                </Badge>
              </Group>
              <Text c="dimmed" size="sm">
                {lrnGroups?.length || 0} LRNs in this batch
              </Text>
            </Stack>
          </Group>

          <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />} p="sm" style={{ maxWidth: 340, flexShrink: 0 }}>
            <Text size="sm">This will simulate sending the selected message(s) to the Customs API for the chosen LRNs.</Text>
          </Alert>
        </Group>
      </Paper>

      {lrnGroups && lrnGroups.length > 0 && (
        <>
          <Paper withBorder radius="md" p="lg">
            <Stack gap="lg">
              <Stack gap={2}>
                <Text fw={700}>Default simulation settings</Text>
                <Text size="sm" c="dimmed">
                  These settings will be applied to all LRNs in this batch unless a row is manually overridden.
                </Text>
              </Stack>
              <Grid>
                <Grid.Col span={3}>
                  <Select
                    label="Simulated message"
                    placeholder="Choose a simulated message"
                    data={statusOptions}
                    value={applyAllConfig.status || null}
                    onChange={(value) => setApplyAllConfig((prev) => ({ ...prev, status: value || undefined }))}
                    searchable
                    clearable
                    size="md"
                  />
                </Grid.Col>
                <Grid.Col span={2} />
                <Grid.Col span={4}>
                  <Group>
                    <Switch
                      checked={applyAllConfig.useCallback}
                      onChange={(event) => {
                        const useCallback = event.currentTarget.checked;
                        setApplyAllConfig((prev) => ({ ...prev, useCallback }));
                      }}
                      disabled={isCallbackDisabled}
                      size="md"
                      label="Send callback"
                    />

                    <NumberInput
                      label="Callback delay"
                      value={applyAllConfig.callbackDelay}
                      onChange={(value) => setApplyAllConfig((prev) => ({ ...prev, callbackDelay: Number(value) || 0 }))}
                      min={0}
                      suffix=" ms"
                      disabled={!applyAllConfig.useCallback}
                    />
                  </Group>
                </Grid.Col>
              </Grid>
            </Stack>
          </Paper>

          <Paper withBorder radius="md" p="md">
            <Stack gap="md">
              <Group justify="space-between" align="flex-end" wrap="wrap">
                <Stack gap={2}>
                  <Group gap="xs">
                    <Text fw={700}>Declarations by LRN</Text>
                    <Badge variant="light" color="gray">
                      {filteredGroups.length}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    Override settings for specific LRNs when you need different values.
                  </Text>
                </Stack>

                <Group gap="sm" wrap="wrap">
                  <MantineTextInput
                    placeholder="Search LRN..."
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.currentTarget.value)}
                    leftSection={<IconSearch size={16} />}
                    style={{ minWidth: 220 }}
                  />
                </Group>
              </Group>

              <ScrollArea h={420} type="auto" offsetScrollbars>
                <Table verticalSpacing="md" horizontalSpacing="md" highlightOnHover withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>LRN</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th>Sequences</Table.Th>
                      <Table.Th>Override</Table.Th>
                      <Table.Th>Simulated message</Table.Th>
                      <Table.Th>Callback</Table.Th>
                      <Table.Th>Delay</Table.Th>
                      <Table.Th w={48} />
                    </Table.Tr>
                  </Table.Thead>

                  <Table.Tbody>
                    {filteredGroups.map((group) => {
                      const rowConfig: LrnConfig = lrnConfigs[group.lrn] || initialConfig;
                      const effectiveConfig = rowConfig.isOverrideEnabled ? rowConfig : applyAllConfig;
                      const isInlineEditing = rowConfig.isOverrideEnabled;

                      return (
                        <Table.Tr key={group.lrn}>
                          <Table.Td>
                            <Text fw={600} ff="monospace" size="sm">
                              {group.lrn}
                            </Text>
                          </Table.Td>

                          <Table.Td>
                            <Badge variant="light" color={getStatusTone(group.currentStatus)}>
                              {prettifyStatus(group.currentStatus)}
                            </Badge>
                          </Table.Td>

                          <Table.Td>{group.sequences.length}</Table.Td>

                          <Table.Td>
                            <Switch
                              checked={rowConfig.isOverrideEnabled}
                              onChange={(event) => handleToggleRowOverride(group.lrn, event.currentTarget.checked)}
                              size="sm"
                            />
                          </Table.Td>

                          <Table.Td miw={220}>
                            {isInlineEditing ? (
                              <Select
                                placeholder="Choose a simulated message"
                                data={statusOptions}
                                value={rowConfig.status || null}
                                onChange={(value) => updateLrnConfig(group.lrn, { status: value || undefined })}
                                searchable
                                clearable
                                size="sm"
                              />
                            ) : (
                              <Text size="sm" c={effectiveConfig.status ? undefined : 'dimmed'}>
                                {effectiveConfig.status ? prettifyStatus(effectiveConfig.status) : 'Not message set'}
                              </Text>
                            )}
                          </Table.Td>

                          <Table.Td miw={120}>
                            {isInlineEditing ? (
                              <Switch
                                checked={rowConfig.useCallback}
                                onChange={(event) => updateLrnConfig(group.lrn, { useCallback: event.currentTarget.checked })}
                                disabled={isCallbackDisabled}
                                size="sm"
                              />
                            ) : (
                              <Text size="sm">
                                {effectiveConfig.useCallback ? (
                                  <IconCircleCheckFilled color={theme.colors.green[6]} />
                                ) : (
                                  <IconXboxXFilled color={theme.colors.red[6]} />
                                )}
                              </Text>
                            )}
                          </Table.Td>

                          <Table.Td miw={160}>
                            {isInlineEditing ? (
                              <NumberInput
                                value={rowConfig.callbackDelay}
                                onChange={(value) => updateLrnConfig(group.lrn, { callbackDelay: Number(value) || 0 })}
                                min={0}
                                suffix=" ms"
                                size="sm"
                              />
                            ) : (
                              <Text size="sm">{effectiveConfig.callbackDelay} ms</Text>
                            )}
                          </Table.Td>

                          <Table.Td>
                            {isInlineEditing ? (
                              <ActionIcon variant="subtle" color="gray" onClick={() => resetLrnConfig(group.lrn)}>
                                <IconRefresh size={16} />
                              </ActionIcon>
                            ) : null}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Stack>
          </Paper>
        </>
      )}

      {lrnGroups?.length === 0 && (
        <Alert icon={<IconAlertCircle size={20} />} color="yellow" variant="light" title="No Declaration Groups">
          No declaration groups were found for this batch. Please ensure the batch has been processed correctly.
        </Alert>
      )}

      <Divider />

      <Group justify="space-between" align="flex-end">
        <Button onClick={onHide} variant="default" size="md">
          Cancel
        </Button>

        <Stack gap={4} align="flex-end">
          <Button onClick={handleConfirmChanges} disabled={!hasChanges || isLoading || isLoadingStatuses} loading={setStatusMutation.isPending} size="md">
            Execute
          </Button>
        </Stack>
      </Group>
    </Stack>
  );
};
